import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCaregiverActivity, fetchCaregiverActivityDetail } from "../utils/api";

/**
 * One bulk request per folder/location view (or a single client on a
 * detail page) — returns
 * { [clientId]: { '7d': {visitCount, caregiverCount}, '30d': {...}, ... } }
 *
 * Pass folderId, locationId, or clientIds (array) — whichever the calling
 * page already has. Precedence: folderId > locationId > clientIds.
 */
export function useCaregiverActivity({
  folderId,
  locationId,
  clientIds,
  enabled = true,
} = {}) {
  const normalizedClientIds = clientIds?.length ? [...clientIds].sort() : null;
  const scopeKey = folderId || locationId || normalizedClientIds?.join(",") || null;

  return useQuery({
    queryKey: [
      "caregiver-activity",
      folderId || null,
      locationId || null,
      normalizedClientIds,
    ],
    queryFn: () => fetchCaregiverActivity({ folderId, locationId, clientIds: normalizedClientIds }),
    enabled: !!scopeKey && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * "Care Count" section detail for a single client: per-window stats +
 * 90-day caregiver breakdown + 13-week visit series for the chart.
 */
export function useCaregiverActivityDetail(clientId, { enabled = true } = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["caregiver-activity-detail", clientId || null],
    queryFn: () => fetchCaregiverActivityDetail(clientId, { refresh: false }),
    enabled: !!clientId && enabled,
    staleTime: 5 * 60 * 1000,
  });

  const refreshFromWellSky = async () => {
    if (!clientId) return;
    await fetchCaregiverActivityDetail(clientId, { refresh: true });
    await queryClient.invalidateQueries({
      queryKey: ["caregiver-activity-detail", clientId],
    });
  };

  return { ...query, refreshFromWellSky };
}

export default useCaregiverActivity;
